import React, { useCallback } from 'react';
import { Calendar, Views } from 'react-big-calendar';
import { format, isSameMonth, parseISO } from 'date-fns';
import { calendarLocalizer, calendarMessages } from '../../../../utils/calendarUtils';
import {
  ExpenseCalendarEvent,
  getExpenseEventColor,
  getPaymentTypeLabel,
  PaymentType as ExpensePaymentType,
} from '../../../../components/ExpenseCalendarEvent/ExpenseCalendarEvent';
import { ActivityList } from '../../../../components/ActivityTimeline/ActivityList';
import { ActivityView, ActivityViewToggle } from '../../../../components/ActivityTimeline/ActivityViewToggle';
import { CalendarToolbar } from '../../../../components/CalendarToolbar/CalendarToolbar';
import { createExpenseSummaryItems } from '../../../../utils/calendarToolbarUtils';
import { ExpenseEventDto } from '../types';
import { useExpensesCalendar } from '../hooks/useExpensesCalendar';
import { dayPropGetter, getCalendarTimeBounds, getExpenseTotals } from '../utils';
import { dataFillProps } from '../../../../utils/dataFill';

export const PaymentsTab = ({
  calendar: expensesCalendar,
  view,
  onViewChange,
}: {
  calendar: ReturnType<typeof useExpensesCalendar>;
  view: ActivityView;
  onViewChange: (view: ActivityView) => void;
}) => {
  const { minTime, maxTime } = getCalendarTimeBounds();

  const expenseEventPropGetter = useCallback((event: ExpenseEventDto) => {
    const color = getExpenseEventColor(event.paymentType);
    return dataFillProps(color);
  }, []);

  const expenses = expensesCalendar.userExpenses?.expenses ?? [];
  const summaryItems = createExpenseSummaryItems(expenses, {
    totalAmount: expensesCalendar.userExpenses?.totalAmount ?? 0,
    totalCashAmount: expensesCalendar.userExpenses?.totalCashAmount ?? 0,
    totalBonusAmount: expensesCalendar.userExpenses?.totalBonusAmount ?? 0,
  });

  const toggle = (
    <div className='activity-view-bar'>
      <ActivityViewToggle name='payments-view' value={view} onChange={onViewChange} />
    </div>
  );

  if (view === 'list') {
    // The answer covers the month grid (with days of the neighbouring months); the list shows the month only.
    const monthExpenses = expenses.filter((expense) =>
      isSameMonth(parseISO(expense.start), expensesCalendar.expensesCalendarDate),
    );
    const items = monthExpenses.map((expense) => {
      const start = parseISO(expense.start);
      // The profile types repeat the payment type enum with the same values.
      const paymentType = expense.paymentType as string as ExpensePaymentType;
      const employee = expense.employee ? `${expense.employee.name} ${expense.employee.surnames}` : undefined;
      return {
        id: expense.id,
        start,
        time: format(start, 'HH:mm'),
        title: expense.title,
        color: getExpenseEventColor(paymentType),
        details: [getPaymentTypeLabel(paymentType), ...(employee ? [employee] : [])],
        aside: { label: `${expense.amount}€`, tone: 'strong' as const },
      };
    });
    return (
      <>
        {toggle}
        <ActivityList
          date={expensesCalendar.expensesCalendarDate}
          onNavigate={expensesCalendar.handleExpensesCalendarNavigate}
          items={items}
          summaryItems={createExpenseSummaryItems(monthExpenses, getExpenseTotals(monthExpenses))}
        />
      </>
    );
  }

  return (
    <>
      {toggle}
      <div className='calendar-container'>
        <Calendar
          dayLayoutAlgorithm='no-overlap'
          localizer={calendarLocalizer}
          events={expenses}
          startAccessor={(event) => parseISO(event.start)}
          endAccessor={(event) => parseISO(event.start)}
          step={30}
          timeslots={1}
          messages={calendarMessages}
          views={[Views.MONTH]}
          view={Views.MONTH}
          date={expensesCalendar.expensesCalendarDate}
          onNavigate={expensesCalendar.handleExpensesCalendarNavigate}
          onView={() => {}}
          max={maxTime}
          min={minTime}
          eventPropGetter={expenseEventPropGetter}
          components={{
            event: (props) => <ExpenseCalendarEvent {...props} />,
            toolbar: (props) => <CalendarToolbar {...props} summaryItems={summaryItems} />,
            showMore: ({ count }) => (
              <div className='rbc-show-more' style={{ cursor: 'default' }}>
                +{count} más
              </div>
            ),
          }}
          dayPropGetter={dayPropGetter}
        />
      </div>
    </>
  );
};
